// DropdownMenu, ContextMenu and Popover — the floating layers. Menus share one item style.
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode, type ElementRef } from 'react'
import * as DM from '@radix-ui/react-dropdown-menu'
import * as CM from '@radix-ui/react-context-menu'
import * as P from '@radix-ui/react-popover'
import { Check, ChevronRight } from 'lucide-react'
import { cn } from './cn'
import { Kbd } from './kbd'

const surface = 'z-[60] min-w-44 overflow-hidden rounded-card border border-border bg-surface-raised p-1 text-text shadow-popover outline-none'
const motion = 'data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out origin-[var(--radix-dropdown-menu-content-transform-origin)]'
const item = (destructive?: boolean) => cn(
  'relative flex h-8 cursor-default select-none items-center gap-2 rounded-[6px] px-2 text-base outline-none',
  'data-[highlighted]:bg-surface-active data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  destructive ? 'text-danger-strong data-[highlighted]:bg-danger-soft [&_svg]:text-danger-strong' : '[&_svg]:text-text-muted',
)

// ─── DropdownMenu ───────────────────────────────────────────────────────────────────────

/**
 * Menu of actions. Compose:
 * <DropdownMenu trigger={<IconButton icon={<Ellipsis/>} label="More"/>}>
 *   <MenuItem icon={<Copy/>} onSelect={dup}>Duplicate</MenuItem>
 *   <MenuSeparator/>
 *   <MenuItem destructive icon={<Trash2/>} onSelect={del}>Delete</MenuItem>
 * </DropdownMenu>
 */
export function DropdownMenu({ trigger, children, align = 'end', side = 'bottom', open, onOpenChange, contentClassName, modal = true }: {
  trigger: ReactNode; children: ReactNode; align?: 'start' | 'center' | 'end'; side?: 'top' | 'bottom' | 'left' | 'right'
  open?: boolean; onOpenChange?: (o: boolean) => void; contentClassName?: string; modal?: boolean
}) {
  return (
    <DM.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content align={align} side={side} sideOffset={4} collisionPadding={8} className={cn(surface, motion, contentClassName)}>
          {children}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  )
}

export interface MenuItemProps extends Omit<ComponentPropsWithoutRef<typeof DM.Item>, 'children'> {
  icon?: ReactNode
  /** Combo hint on the right ("mod+d"). */
  shortcut?: string
  /** Red styling for irreversible actions. */
  destructive?: boolean
  /** Secondary line under the label. */
  description?: ReactNode
  children: ReactNode
}
export const MenuItem = forwardRef<ElementRef<typeof DM.Item>, MenuItemProps>(function MenuItem({ icon, shortcut, destructive, description, className, children, ...props }, ref) {
  return (
    <DM.Item ref={ref} className={cn(item(destructive), description && 'h-auto min-h-8 py-2', className)} {...props}>
      {icon}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{children}</span>
        {description && <span className="text-sm text-text-muted">{description}</span>}
      </span>
      {shortcut && <Kbd combo={shortcut} className="ml-auto" />}
    </DM.Item>
  )
})

export function MenuCheckboxItem({ checked, onCheckedChange, children, disabled, onSelect }: { checked: boolean; onCheckedChange: (v: boolean) => void; children: ReactNode; disabled?: boolean; onSelect?: (e: Event) => void }) {
  return (
    <DM.CheckboxItem checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} onSelect={onSelect} className={cn(item(), 'pr-8')}>
      <span className="truncate">{children}</span>
      <DM.ItemIndicator className="absolute right-2 text-primary-strong"><Check className="text-primary-strong!" /></DM.ItemIndicator>
    </DM.CheckboxItem>
  )
}

export function MenuRadioGroup<V extends string>({ value, onValueChange, options }: { value: V; onValueChange: (v: V) => void; options: Array<{ value: V; label: ReactNode; icon?: ReactNode }> }) {
  return (
    <DM.RadioGroup value={value} onValueChange={v => onValueChange(v as V)}>
      {options.map(o => (
        <DM.RadioItem key={o.value} value={o.value} className={cn(item(), 'pr-8')}>
          {o.icon}
          <span className="truncate">{o.label}</span>
          <DM.ItemIndicator className="absolute right-2"><Check className="text-primary-strong!" /></DM.ItemIndicator>
        </DM.RadioItem>
      ))}
    </DM.RadioGroup>
  )
}

export function MenuLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <DM.Label className={cn('px-2 pt-2 pb-1 text-xs font-medium text-text-subtle', className)}>{children}</DM.Label>
}
export function MenuSeparator() { return <DM.Separator className="-mx-1 my-1 h-px bg-border" /> }

export function MenuSub({ label, icon, children }: { label: ReactNode; icon?: ReactNode; children: ReactNode }) {
  return (
    <DM.Sub>
      <DM.SubTrigger className={cn(item(), 'data-[state=open]:bg-surface-active')}>
        {icon}<span className="flex-1 truncate">{label}</span><ChevronRight className="ml-auto" />
      </DM.SubTrigger>
      <DM.Portal>
        <DM.SubContent sideOffset={6} collisionPadding={8} className={cn(surface, 'data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out')}>{children}</DM.SubContent>
      </DM.Portal>
    </DM.Sub>
  )
}

// ─── ContextMenu ────────────────────────────────────────────────────────────────────────

/**
 * Right-click menu around any element (table rows, media tiles). Items:
 * `items={[{ label:'Open', icon:<ExternalLink/>, onSelect }, 'separator', { label:'Delete', destructive:true, onSelect }]}`
 */
export interface ContextMenuItemDef { label: ReactNode; icon?: ReactNode; onSelect: () => void; destructive?: boolean; disabled?: boolean; shortcut?: string }
export function ContextMenu({ children, items, disabled }: { children: ReactNode; items: Array<ContextMenuItemDef | 'separator'>; disabled?: boolean }) {
  if (disabled) return <>{children}</>
  return (
    <CM.Root>
      <CM.Trigger asChild>{children}</CM.Trigger>
      <CM.Portal>
        <CM.Content collisionPadding={8} className={cn(surface, 'data-[state=open]:animate-pop-in')}>
          {items.map((it, i) => it === 'separator' ? <CM.Separator key={i} className="-mx-1 my-1 h-px bg-border" /> : (
            <CM.Item key={i} disabled={it.disabled} onSelect={it.onSelect} className={item(it.destructive)}>
              {it.icon}<span className="flex-1 truncate">{it.label}</span>
              {it.shortcut && <Kbd combo={it.shortcut} className="ml-auto" />}
            </CM.Item>
          ))}
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  )
}

// ─── Popover ────────────────────────────────────────────────────────────────────────────

export interface PopoverProps {
  trigger: ReactNode
  children: ReactNode
  open?: boolean
  onOpenChange?: (o: boolean) => void
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'bottom' | 'left' | 'right'
  /** Tailwind width class for the panel (default w-72). */
  className?: string
  /** Element inside to focus on open (default: first focusable). */
  onOpenAutoFocus?: (e: Event) => void
  modal?: boolean
  /** Anchor to something other than the trigger. */
  anchor?: ReactNode
}

/** Floating panel for small forms and pickers: `<Popover trigger={<Button>Filter</Button>}>…</Popover>`. */
export function Popover({ trigger, children, open, onOpenChange, align = 'start', side = 'bottom', className, onOpenAutoFocus, modal, anchor }: PopoverProps) {
  return (
    <P.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      {anchor && <P.Anchor asChild>{anchor}</P.Anchor>}
      <P.Trigger asChild>{trigger}</P.Trigger>
      <P.Portal>
        <P.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={onOpenAutoFocus}
          className={cn('z-[60] w-72 max-w-[calc(100vw-16px)] rounded-card border border-border bg-surface-raised p-3 text-text shadow-popover outline-none',
            'data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out', className)}
        >
          {children}
        </P.Content>
      </P.Portal>
    </P.Root>
  )
}
export const PopoverClose = P.Close
